<template>
  <div class="tt-page">
    <TripPageHeader
      title="My Trips"
      subtitle="Trips assigned to you — start before leaving, close when you're back"
      icon="bi-compass"
    >
      <template #actions>
        <button class="btn btn-sm tt-btn-ghost" :disabled="loading" @click="refresh">
          <i class="bi bi-arrow-clockwise mr-1"></i>Refresh
        </button>
      </template>
    </TripPageHeader>

    <div class="tt-tabs">
      <button class="tt-tab" :class="{ active: tab === 'active' }" @click="switchTab('active')">Active</button>
      <button class="tt-tab" :class="{ active: tab === 'history' }" @click="switchTab('history')">Completed</button>
    </div>

    <div class="tt-card">
      <div class="tt-card-body">
        <div class="table-responsive">
          <table class="table table-sm tt-table mb-0">
            <thead>
              <tr>
                <th>Trip Date</th><th>Places</th><th>Purpose</th><th>Vehicle</th>
                <th>Status</th><th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="6" class="tt-empty">
                  <span class="spinner-border spinner-border-sm mr-2"></span>Loading…
                </td>
              </tr>
              <tr v-else-if="!trips.length">
                <td colspan="6" class="tt-empty">
                  <i class="bi bi-compass tt-empty-icon"></i>
                  {{ tab === "history" ? "No completed trips yet." : "No trips assigned to you right now." }}
                </td>
              </tr>
              <tr v-for="t in trips" :key="t.id">
                <td class="text-nowrap">{{ fmtTripDate(t.trip_date) }}</td>
                <td class="tt-purpose" :title="placesLabel(t)">{{ placesLabel(t) }}</td>
                <td class="tt-purpose" :title="t.purpose">{{ t.purpose }}</td>
                <td>{{ t.vehicle ? t.vehicle.vehicle_name : "—" }}</td>
                <td><span :class="tripStatusClass(t.status)">{{ statusLabel(t.status) }}</span></td>
                <td class="text-right text-nowrap">
                  <button class="tt-btn-icon" @click="viewTrip(t)" title="View"><i class="bi bi-eye"></i></button>
                  <button v-if="canPrintTrip(t)" class="tt-btn-icon ml-1" @click="printTrip(t)" title="Print"><i class="bi bi-printer"></i></button>
                  <button v-if="canPrintGatepass(t)" class="tt-btn-icon ml-1" @click="printGatepass(t)" title="Print gatepass"><i class="bi bi-shield-check"></i></button>
                  <button v-if="t.status === 'approved'" class="btn btn-sm tt-btn-primary ml-1" @click="openStart(t)">Start Trip</button>
                  <button v-if="canCancelTrip(t, currentUser)" class="btn btn-sm btn-outline-danger ml-1" @click="cancel(t)">Cancel</button>
                  <button v-if="t.status === 'ongoing'" class="btn btn-sm tt-btn-ghost ml-1" @click="openPassengers(t)">Passengers</button>
                  <button v-if="t.status === 'ongoing'" class="btn btn-sm tt-btn-ghost ml-1" @click="openAddStop(t)">Add Stop</button>
                  <button v-if="t.status === 'ongoing'" class="btn btn-sm btn-outline-warning ml-1" @click="undoStart(t)">Undo Start</button>
                  <button v-if="t.status === 'ongoing'" class="btn btn-sm btn-outline-success ml-1" @click="openClose(t)">Close Trip</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <TripPagination :pagination="pagination" @page="goToPage" />
      </div>
    </div>

    <TripStartModal
      :open="startOpen"
      :trip="activeTrip"
      @close="startOpen = false"
      @done="fetchQueue"
    />
    <TripAddStopModal
      :open="stopOpen"
      :trip="activeTrip"
      @close="stopOpen = false"
      @done="onStopDone"
    />
    <TripPassengersModal
      :open="passengersOpen"
      :trip="activeTrip"
      @close="passengersOpen = false"
      @done="onPassengersDone"
    />
    <TripCloseModal
      :open="closeOpen"
      :trip="activeTrip"
      @close="closeOpen = false"
      @done="fetchQueue"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import TripPageHeader from "./components/TripPageHeader.vue";
import TripPagination from "./components/TripPagination.vue";
import TripStartModal from "./components/TripStartModal.vue";
import TripPassengersModal from "./components/TripPassengersModal.vue";
import TripAddStopModal from "./components/TripAddStopModal.vue";
import TripCloseModal from "./components/TripCloseModal.vue";
import { fmtTripDate, tripStatusClass, canPrintTrip, canPrintGatepass, canCancelTrip, placesLabel, tripApiError } from "./tripHelpers";

export default {
  name: "TripDriverQueue",
  components: { TripPageHeader, TripPagination, TripStartModal, TripPassengersModal, TripAddStopModal, TripCloseModal },
  data() {
    return {
      tab: "active",
      startOpen: false,
      passengersOpen: false,
      stopOpen: false,
      closeOpen: false,
      activeTrip: null,
    };
  },
  computed: {
    ...mapState("trips", {
      trips: "driverQueue",
      loading: "loading",
      pagination: "driverQueuePagination",
    }),
    currentUser() {
      return this.$store.state.user || {};
    },
  },
  methods: {
    canCancelTrip,
    fmtTripDate,
    tripStatusClass,
    canPrintTrip,
    canPrintGatepass,
    placesLabel,
    statusLabel(s) {
      return { approved: "Ready to Start", ongoing: "Ongoing", completed: "Completed" }[s] || s;
    },
    queueName() {
      return this.tab === "history" ? "driver_history" : "driver";
    },
    refresh() {
      this.fetchQueue();
    },
    switchTab(tab) {
      this.tab = tab;
      this.fetchQueue(1);
    },
    fetchQueue(page = 1) {
      this.$store.dispatch("trips/fetchTrips", {
        list: "driver",
        actionable: 1,
        queue: this.queueName(),
        page,
      });
    },
    goToPage(page) {
      this.fetchQueue(page);
    },
    viewTrip(t) {
      this.$router.push({ name: "TripView", params: { id: t.id }, query: { returnTo: "/trips/my-trips" } });
    },
    printTrip(t) {
      const routeData = this.$router.resolve({
        name: "TripPrint",
        params: { id: t.id },
        query: { returnTo: "/trips/my-trips" },
      });
      window.open(routeData.href, "_blank");
    },
    printGatepass(t) {
      const routeData = this.$router.resolve({
        name: "TripGatepassPrint",
        params: { id: t.id },
        query: { returnTo: "/trips/my-trips" },
      });
      window.open(routeData.href, "_blank");
    },
    openStart(t) {
      this.$store.dispatch("trips/fetchTrip", t.id).then((full) => {
        this.activeTrip = full || t;
        this.startOpen = true;
      });
    },
    openClose(t) {
      this.activeTrip = t;
      this.closeOpen = true;
    },
    openPassengers(t) {
      this.$store.dispatch("trips/fetchTrip", t.id).then((full) => {
        this.activeTrip = full || t;
        this.passengersOpen = true;
      });
    },
    openAddStop(t) {
      this.$store.dispatch("trips/fetchTrip", t.id).then((full) => {
        this.activeTrip = full || t;
        this.stopOpen = true;
      });
    },
    onStopDone() {
      this.fetchQueue();
      if (this.activeTrip && this.activeTrip.id) {
        this.$store.dispatch("trips/fetchTrip", this.activeTrip.id).then((full) => {
          this.activeTrip = full || this.activeTrip;
        });
      }
    },
    onPassengersDone() {
      this.fetchQueue();
      if (this.activeTrip && this.activeTrip.id) {
        this.$store.dispatch("trips/fetchTrip", this.activeTrip.id).then((full) => {
          this.activeTrip = full || this.activeTrip;
        });
      }
    },
    undoStart(t) {
      if (!confirm("Undo start? The trip goes back to Approved so you can start again with the correct details.")) {
        return;
      }
      this.$store
        .dispatch("trips/undoTripStart", t.id)
        .then(() => {
          toastr.success("Trip sent back to Approved.", "Success");
          this.fetchQueue();
        })
        .catch((e) => toastr.error(tripApiError(e), "Error"));
    },
    cancel(t) {
      if (!confirm("Cancel this approved trip? It will no longer be available to start.")) return;
      this.$store
        .dispatch("trips/cancelTrip", t.id)
        .then(() => {
          toastr.success("Trip cancelled.", "Success");
          this.fetchQueue();
        })
        .catch((e) => toastr.error(tripApiError(e), "Error"));
    },
  },
  mounted() {
    this.fetchQueue();
    const action = this.$route.query.action;
    const id = this.$route.query.id;
    if (id && (action === "start" || action === "close")) {
      this.$store.dispatch("trips/fetchTrip", id).then((t) => {
        if (!t) return;
        if (action === "start" && t.status === "approved") this.openStart(t);
        if (action === "close" && t.status === "ongoing") this.openClose(t);
      });
    }
  },
};
</script>
